import ModalContainer, { ModalContainerProps } from "components/common/ModalContainer";
import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { useCoAgent } from "@copilotkit/react-core";
import { useSearchParams } from "react-router-dom";

import { ForecastNewProductContentProps } from "./types/ForecastNewProductStepForm";
import Button from "components/common/Button";
import Loader from "components/common/Loader";
import { AgentType } from "enums/AgentTypeEnum";
import ForecastNewProductPlanError from "./ForecastNewProductPlanError";
import { ForecastNewProductPlanErrorType } from "./enums/ForecastNewProductPlanErrorType";
import { FORECAST_NEW_PRODUCT_STEP } from "./constant/ForecastNewProductStepConstant";

type ForecastNewProductPlanLoaderProps = {} & ForecastNewProductContentProps &
    Omit<ModalContainerProps, "children">;

export default function ForecastNewProductPlanLoader(props: ForecastNewProductPlanLoaderProps) {
    const { stepper, formik, tailwindClassName, ...rest } = props;
    const [searchParams, setSearchParams] = useSearchParams();
    const isRerun = searchParams?.get("runType") === "rerun";
    const newRerun = searchParams?.get("runType") === "newRun";

    const [hasRun, setHasRun] = useState(false);

    const [error, setError] = useState<{
        errorType: ForecastNewProductPlanErrorType;
        errorMessage: string;
        error: string;
    } | null>(null);

    const agentData = {
        name: AgentType.NewProductForecaster,
        ...(newRerun && {
            initialState: {
                newProductPlanId: formik.values.newProductPlanId,
                running: true,
            },
        }),
        config: {
            subgraphs: true,
            configurable: {
                ldbId: formik.values.ldbId,
                thread_id: formik.values.threadId,
            },
            limit: 50,
        },
    };

    console.log({ agentData });
    const agent = useCoAgent<any>({ ...agentData });

    const agentState = agent.state;
    const isError = agentState?.error;

    useEffect(() => {
        if (
            agent.nodeName === "__end__" &&
            !agentState?.running &&
            !error &&
            Object.keys(agentState).length > 0 &&
            hasRun
        ) {
            stepper.next();
        }
    }, [isError, agentState.running, hasRun, agent.nodeName]);

    useEffect(() => {
        if (isRerun || newRerun) {
            agent.setState({
                ...(newRerun && {
                    newProductPlanId: formik.values.newProductPlanId,
                }),
                running: true,
            });
            agent.run();
        }
        setHasRun(true);
    }, []);

    useEffect(() => {
        if (isError && !error) {
            setError({
                errorType: agentState?.errorType,
                errorMessage: agentState.errorMessage,
                error: agentState.error,
            });
        }
    }, [isError]);

    console.log({
        agent,
        agentState,
        newProductPlanId: formik.values.newProductPlanId,
        threadId: formik.values.threadId,
        ldbId: formik.values.ldbId,
        agentData,
    });

    return (
        <ModalContainer {...rest} tailwindClassName={clsx("max-w-[640px]", tailwindClassName)}>
            {isError ? (
                <ForecastNewProductPlanError
                    errorType={error?.errorType}
                    error={error?.errorMessage}
                    onClose={() => {
                        rest?.closeModal();
                    }}
                    action={() => {
                        if (agentState?.errorType === ForecastNewProductPlanErrorType.system) {
                            agent.run();
                        } else {
                            stepper.go(FORECAST_NEW_PRODUCT_STEP.PLAN_EDIT);
                            setSearchParams({ newProductPlanId: formik.values.newProductPlanId });
                        }
                    }}
                />
            ) : (
                <div className="p-[60px] flex flex-col items-center">
                    <Loader size={8} isGradient />

                    <div className="flex justify-between gap-2 items-center px-[24px] py-[16px]">
                        <i className="ri-sparkling-fill text-xl text-center inline-block text-transparent bg-clip-text bg-gradient-to-tr  from-[#62CC00] from-0% to-[#FFB902] to-100%"></i>

                        <p className="text-[18px]  font-rocGroteskMedium">
                            Planning your forecast...
                        </p>
                    </div>

                    <p className="text-center text-sm text-slate-500">
                        Synth is analyzing your product details, reviewing similar product patterns,
                        and applying relevant forecasting methods. This may take a few moments as we
                        tailor the most accurate forecast based on your input and available data.
                    </p>
                    <div className="flex justify-content-center gap-2">
                        <Button
                            isLoading={formik.isSubmitting}
                            onClick={() => {
                                formik.handleSubmit();
                                agent.stop();
                            }}
                            btnText="Cancel Planing"
                            btnClassname="!bg-[#F4F5F7] mx-auto mt-6 cursor-pointer !text-[13px] !px-[24px] !py-2.5 rounded-[5px] !h-[42px] !whitespace-nowrap"
                        />
                        <Button
                            isLoading={formik.isSubmitting}
                            onClick={() => {
                                rest.closeModal();
                            }}
                            btnText="Close"
                            btnClassname="mx-auto mt-6 cursor-pointer !text-[13px] !px-[24px] !py-2.5 rounded-[5px] !h-[42px] !whitespace-nowrap"
                        />
                    </div>
                </div>
            )}
        </ModalContainer>
    );
}
